/**
 * "My location" GPS jump (issue #161): converts a one-shot WGS84 fix (+
 * optional compass heading) into a `jumpToViewpoint`-shaped `CameraPose`.
 * DOM-free — no `navigator`/geolocation/orientation access here, so the
 * lat/lon -> pose maths is unit-testable in plain Node without a browser;
 * `myLocationControl.ts` is the browser-facing glue that acquires the fix
 * and calls into this module.
 *
 * Coordinate path: `wgs84ToBng` (core/visualProjection.ts) then `bngToLocal`
 * (core/coordinates.ts) — the same visual-only proj4 transform the GPX route
 * ribbon already uses (issue #40's consolidation), never a second,
 * independently-parameterised definition.
 *
 * Hard constraints #6/#7: vertical placement only ever comes from
 * `sampleGround` (the DTM-derived collider); a fix outside every loaded
 * tile is refused outright — the camera stays put — never dropped at a
 * fabricated y=0.
 */
import { AOI_CENTRE_EASTING, AOI_CENTRE_NORTHING, bearingDegFromForward, bngToLocal, type LocalXZ } from '../core/coordinates';
import { wgs84ToBng } from '../core/visualProjection';
import { EYE_HEIGHT_M, type GroundSampler } from './groundLock';
import type { CameraPose } from './viewpointJump';

/** Above this reported GPS accuracy radius (metres), a fix is still used but
 * flagged as approximate (issue #161's "poor accuracy" row) — never refused
 * outright, since even a loose fix is more useful than none. */
export const POOR_ACCURACY_M = 500;

export interface MyLocationFix {
  /** WGS84 latitude, degrees. */
  latitude: number;
  /** WGS84 longitude, degrees. */
  longitude: number;
  /** Reported GPS accuracy radius, metres (Geolocation API `coords.accuracy`). */
  accuracyM?: number;
  /** Compass heading, degrees, 0 = north, clockwise (device orientation). Absent means no compass reading was available. */
  headingDeg?: number;
}

/**
 * Failure kinds spanning both this module (`outside_aoi`) and the browser
 * glue in `myLocationControl.ts` (the geolocation-acquisition failures) —
 * one shared type so `main.ts`'s status-message handling covers every row
 * of issue #161's predictable-failure table from a single union.
 */
export type MyLocationFailureReason =
  | 'permission_denied'
  | 'position_unavailable'
  | 'timeout'
  | 'insecure_context'
  | 'outside_aoi';

export interface MyLocationSuccess {
  kind: 'success';
  pose: CameraPose;
  /** True when `headingDeg` was absent and the bearing defaulted toward the AOI centre. */
  headingIsDefault: boolean;
  /** True when the reported accuracy exceeded `POOR_ACCURACY_M`. */
  approximate: boolean;
  /** User-facing confirmation, e.g. "Placed you at your location, facing W." */
  message: string;
}

export interface MyLocationFailure {
  kind: 'failure';
  reason: MyLocationFailureReason;
  /** User-facing, non-blocking explanation — the camera is left untouched. */
  message: string;
}

export type MyLocationResult = MyLocationSuccess | MyLocationFailure;

/** A closed building footprint ring in LOCAL x/z (already run through
 * `bngToLocal` by the caller — this module stays three.js/DOM-free, so it
 * never sees a `Building` record or a mesh, only plain point arrays; see
 * `main.ts`'s call site, which maps `buildings.json`'s `footprint_bng` via
 * `footprintToLocal` from `scene/buildings.ts`). */
export type FootprintRing = LocalXZ[];

/** Nudge distance (metres) applied when moving a fix from inside a building
 * footprint to just outside it (issue #167) — the user should stand beside
 * the building, not exactly on its wall line. */
const FOOTPRINT_NUDGE_MARGIN_M = 3;

/** Bounded search parameters for the nearest-exterior-point search (issue
 * #167's "no unbounded loop" requirement): a fixed number of concentric
 * rings at a fixed number of angles each, so the search is always exactly
 * `NUDGE_RADIUS_STEPS * NUDGE_ANGLE_STEPS` point-in-polygon checks (per
 * footprint), never more. */
const NUDGE_RADIUS_STEPS = 12;
const NUDGE_ANGLE_STEPS = 16;
const NUDGE_RADIUS_STEP_M = 4;

/** Ray-casting point-in-polygon test against a closed ring (first point
 * implicitly joined back to the last, matching buildings.json's "open ring"
 * convention used elsewhere in the viewer, e.g. buildWalls in
 * scene/buildings.ts). Points exactly on the boundary may read either way —
 * acceptable here since the nudge search only needs "clear of the building",
 * not an exact boundary classification. */
function pointInPolygon(x: number, z: number, ring: FootprintRing): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const intersects = a.z > z !== b.z > z && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** True when {x,z} sits inside (or on) at least one of `footprints`. */
function insideAnyFootprint(x: number, z: number, footprints: readonly FootprintRing[]): boolean {
  return footprints.some((ring) => ring.length >= 3 && pointInPolygon(x, z, ring));
}

/**
 * Nudges {x,z} to the nearest point clear of every footprint in
 * `footprints`, within a bounded search (issue #167). When {x,z} is not
 * inside any footprint, it is returned unchanged (open-ground regression:
 * identical pose to before this feature existed). When it IS inside one or
 * more footprints, this walks a fixed, small grid of candidate points
 * (concentric rings at NUDGE_RADIUS_STEP_M spacing, NUDGE_ANGLE_STEPS
 * directions each) outward from {x,z} and returns the first candidate that
 * clears every footprint. If none is found within the bounded search (e.g. a
 * footprint far larger than the search radius), {x,z} is returned unchanged
 * — the caller then proceeds exactly as if no nudge had been attempted,
 * never hanging in an unbounded loop.
 */
function nudgeOutsideFootprints(x: number, z: number, footprints: readonly FootprintRing[]): { x: number; z: number } {
  if (footprints.length === 0 || !insideAnyFootprint(x, z, footprints)) return { x, z };

  for (let r = 1; r <= NUDGE_RADIUS_STEPS; r++) {
    const radius = FOOTPRINT_NUDGE_MARGIN_M + r * NUDGE_RADIUS_STEP_M;
    for (let a = 0; a < NUDGE_ANGLE_STEPS; a++) {
      const angle = (a / NUDGE_ANGLE_STEPS) * 2 * Math.PI;
      const candidateX = x + Math.cos(angle) * radius;
      const candidateZ = z + Math.sin(angle) * radius;
      if (!insideAnyFootprint(candidateX, candidateZ, footprints)) {
        return { x: candidateX, z: candidateZ };
      }
    }
  }
  // Bounded search exhausted (issue #167's fallback): never hang, place at
  // the original fix — same as if no footprint data had been supplied.
  return { x, z };
}

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/** Nearest 8-point compass label for a bearing (issue #161's own example: 270° -> "W"). */
function compassPointLabel(bearingDeg: number): string {
  const idx = Math.round(bearingDeg / 45) % COMPASS_POINTS.length;
  return COMPASS_POINTS[idx];
}

function normalizeBearingDeg(bearingDeg: number): number {
  return ((bearingDeg % 360) + 360) % 360;
}

/** Default bearing (issue #161's "no orientation" row): faces the AOI centre
 * from the fix's own local position, rather than an arbitrary fixed heading. */
function bearingTowardAoiCentre(x: number, z: number): number {
  const centre = bngToLocal(AOI_CENTRE_EASTING, AOI_CENTRE_NORTHING);
  return bearingDegFromForward(centre.x - x, centre.z - z);
}

function buildMessage(headingIsDefault: boolean, approximate: boolean, accuracyM: number | undefined, bearingDeg: number): string {
  const facing = headingIsDefault
    ? 'facing the AOI centre (no compass heading available)'
    : `facing ${compassPointLabel(bearingDeg)}`;
  const accuracyNote =
    approximate && typeof accuracyM === 'number' ? ` — GPS accuracy is only ~${Math.round(accuracyM)} m, so this is approximate` : '';
  return `Placed you at your location, ${facing}${accuracyNote}.`;
}

/**
 * Resolves a GPS fix (+ optional compass heading) to a camera pose, mirroring
 * `poseForViewpoint`'s shape: `sampleGround` supplies the only permitted
 * vertical placement, and `currentFovDeg` (the camera's live vertical FOV) is
 * carried through unchanged — a live GPS fix has no `fov_deg` of its own to
 * apply. Returns an `outside_aoi` failure, camera-untouched, when no loaded
 * terrain tile covers the fix (hard constraints #6/#7) — never a fabricated
 * ground height.
 */
export function poseForMyLocation(
  fix: MyLocationFix,
  sampleGround: GroundSampler,
  currentFovDeg: number,
  buildingFootprints: readonly FootprintRing[] = [],
): MyLocationResult {
  const { easting, northing } = wgs84ToBng(fix.longitude, fix.latitude);
  const fixLocal = bngToLocal(easting, northing);
  const { x, z } = nudgeOutsideFootprints(fixLocal.x, fixLocal.z, buildingFootprints);
  const ground = sampleGround(x, z);
  if (ground === null) {
    return {
      kind: 'failure',
      reason: 'outside_aoi',
      message: 'You are outside the modelled Ray II area — no terrain is loaded at your GPS position, so the camera has not moved.',
    };
  }

  const headingIsDefault = fix.headingDeg === undefined;
  const bearingDeg = headingIsDefault ? bearingTowardAoiCentre(x, z) : normalizeBearingDeg(fix.headingDeg!);
  const approximate = typeof fix.accuracyM === 'number' && fix.accuracyM > POOR_ACCURACY_M;

  const pose: CameraPose = {
    x,
    y: ground + EYE_HEIGHT_M,
    z,
    bearingDeg,
    fovDeg: currentFovDeg,
  };

  return {
    kind: 'success',
    pose,
    headingIsDefault,
    approximate,
    message: buildMessage(headingIsDefault, approximate, fix.accuracyM, bearingDeg),
  };
}
